import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../Modal/Modal';
import Tabs from '../Tabs/Tabs';
import Flex from '../Flex/Flex';
import Button from '../forms/Button/Button';
import ResubscribeTab from './ResubscribeTab';
import { getPrimaryPlans, resubscribe } from '@/api/billing';
import { RecurlyPlan } from '@/lib/recurly';
import styles from './ResubscribeModal.module.css';

interface ResubscribeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReactivate: (planCode: string) => void;
  onEditBilling?: () => void;
  currentPlan?: {
    name: string;
    code: string;
  };
  currentSubscriptionId?: string;
  billingInfo?: {
    lastFour: string;
    cardType: string;
    name?: string;
    expirationMonth?: string;
    expirationYear?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    startedOn?: string;
  };
}

const PROFESSIONAL_FEATURES = [
  { label: "Everything in Standard Plan PLUS", checked: true },
  { label: "LTL Shipments", checked: true },
  { label: "2D Barcodes", checked: true },
  { label: "Early access to Beta features", checked: true }
];

const STANDARD_FEATURES = [
  { label: "Optimized Shipment Flow", checked: true },
  { label: "Inventory Management", checked: true },
  { label: "Shipments Manager", checked: true },
  { label: "Sales Analytics", checked: true }
];

export const ResubscribeModal = ({
  open,
  onOpenChange,
  onReactivate,
  onEditBilling,
  currentPlan,
  currentSubscriptionId,
  billingInfo
}: ResubscribeModalProps) => {
  const [plans, setPlans] = useState<RecurlyPlan[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  
  // Initialize states based on current plan
  const getInitialPlanType = () => {
    if (currentPlan?.code?.includes('professional')) return 'professional';
    return 'standard';
  };
  
  const getInitialBillingType = () => {
    if (currentPlan?.code?.includes('annual')) return 'annual';
    return 'monthly';
  };
  
  const [selectedPlan, setSelectedPlan] = useState<'professional' | 'standard'>(getInitialPlanType());
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'annual'>(getInitialBillingType());
  
  useEffect(() => {
    if (open && !plans) {
      setLoading(true);
      getPrimaryPlans()
        .then((fetchedPlans) => {
          setPlans(fetchedPlans);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch plans:', error);
          setLoading(false);
        });
    }
  }, [open, plans]);
  
  // Get plan data based on selection
  const getPlanData = (planType: 'professional' | 'standard', billingType: 'monthly' | 'annual') => {
    if (!plans) return null;
    
    const planCode = `${planType}-${billingType}-launchspecial`;
    return plans.find(plan => plan.code === planCode);
  };
  
  const getCurrentPrice = (planType: 'professional' | 'standard', billingType: 'monthly' | 'annual') => {
    const plan = getPlanData(planType, billingType);
    return plan?.currencies?.[0]?.unit_amount ? (plan.currencies[0].unit_amount).toFixed(2) : '0.00';
  };

  const planTabs = [
    {
      key: 'professional',
      label: 'Professional',
      children: (
        <ResubscribeTab
          planName="Professional - Launch Special"
          planDisplayName="Professional - Launch Special"
          price={getCurrentPrice('professional', selectedBilling)}
          billingPeriod={selectedBilling}
          billingPeriodText={selectedBilling === 'monthly' ? 'Monthly' : 'Annually'}
          features={PROFESSIONAL_FEATURES}
          selectedBilling={selectedBilling}
          onBillingChange={setSelectedBilling}
          billingInfo={billingInfo}
          onEditBilling={onEditBilling}
          showBillingToggle
        />
      )
    },
    {
      key: 'standard',
      label: 'Standard',
      children: (
        <ResubscribeTab
          planName="Standard"
          planDisplayName="Standard"
          price={getCurrentPrice('standard', selectedBilling)}
          billingPeriod={selectedBilling}
          billingPeriodText={selectedBilling === 'monthly' ? 'Monthly' : 'Annually'}
          features={STANDARD_FEATURES}
          selectedBilling={selectedBilling}
          onBillingChange={setSelectedBilling}
          billingInfo={billingInfo}
          onEditBilling={onEditBilling}
          showBillingToggle={true}
        />
      )
    }
  ];

  const handleReactivate = async () => {
    const planCode = `${selectedPlan}-${selectedBilling}-launchspecial`;
    
    if (!currentSubscriptionId) {
      console.error('No current subscription ID provided');
      return;
    }
    
    setReactivating(true);
    try {
      const response = await resubscribe({ 
        planCode, 
        currentSubscriptionId 
      });
      
      if (response.success) {
        // Call the original callback to close modal and refresh data
        onReactivate(planCode);
      } else {
        console.error('Resubscribe failed:', response.message);
      }
    } catch (error) {
      console.error('Error resubscribing:', error);
    } finally {
      setReactivating(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className={styles.modal}
      style={{ width: '751px' }}
    >
      <ModalBody className={styles.modalBodyCustom}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <div>Loading plans...</div>
          </div>
        ) : (
          <Tabs
            tabs={planTabs}
            activeTab={selectedPlan}
            setActiveTab={(tab) => setSelectedPlan(tab as 'professional' | 'standard')}
            stretchTabsToCover={true}
          />
        )}
      </ModalBody>

      <ModalFooter className={styles.modalFooterCustom} style={{}}>
        <Flex justify="space-between" gap={4}>
          <Button type="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleReactivate} disabled={reactivating}>
            {reactivating ? 'Reactivating...' : 'Reactivate Subscription'}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default ResubscribeModal;